import type { GetJoinStateResponse } from "@/modules/coaching/join/services/getJoinState";

import { QueryClient } from "@tanstack/react-query";
import { isRedirect, redirect } from "@tanstack/react-router";

import { SessionJoinState } from "@/modules/coaching/join/enums/SessionJoinState";
import {
  getJoinStateQuery,
  joinStateKeys,
} from "@/modules/coaching/join/queries/getJoinStateQuery";
import { ApiError } from "@/modules/shared/utils/ApiError";
import { isFetchNetworkError } from "@/modules/shared/utils/reactQueryErrorTracking";
import { captureException } from "@/modules/shared/utils/sentry";
import { DEFAULT_ENTRY_POINT } from "@/modules/tracking/consts/EntryPoints";
import { SHORT_URL_EVENTS } from "@/modules/tracking/consts/Events";
import {
  ShortUrlFlow,
  ShortUrlMeetRedirectTrigger,
} from "@/modules/tracking/consts/ShortUrl";
import { abandonPreviousShortUrlFlowIfDifferent } from "@/modules/tracking/utils/abandonPreviousShortUrlFlow";
import {
  getShortUrlResolutionFromError,
  getShortUrlResolutionFromSuccess,
} from "@/modules/tracking/utils/getShortUrlResolutionStatus";
import { identifyMember } from "@/modules/tracking/utils/identifyMember";
import { readSourceFromLocation } from "@/modules/tracking/utils/readSourceFromLocation";
import { registerSuperProperties } from "@/modules/tracking/utils/registerSuperProperties";
import {
  endShortUrlFlowSession,
  isSameShortUrlFlowVisit,
  startShortUrlFlowSession,
} from "@/modules/tracking/utils/shortUrlFlowSession";
import { trackShortUrlEvent } from "@/modules/tracking/utils/trackShortUrlEvent";

import { getSessionJoinState } from "./getSessionJoinState";
import { LAST_JOINED_MEETING_URL_KEY } from "./joinStorageKeys";
import { redirectToMeet } from "./redirectToMeet";

type JoinScreenLoaderArgs = {
  context: { queryClient: QueryClient };
  params: { sessionCode: string };
};

const fetchJoinState = async (
  queryClient: QueryClient,
  sessionCode: string,
) => {
  try {
    const data = await queryClient.fetchQuery(getJoinStateQuery(sessionCode));
    return { data, isFromCache: false };
  } catch (error) {
    const isServerError =
      error instanceof ApiError && error.errorClass === "5xx";
    const shouldUseCache = isServerError || isFetchNetworkError(error);

    if (shouldUseCache) {
      const cachedData = queryClient.getQueryData<GetJoinStateResponse>(
        joinStateKeys.byCode(sessionCode),
      );

      if (cachedData) {
        return { data: cachedData, isFromCache: true };
      }
    }

    throw error;
  }
};

export const joinScreenLoader = async ({
  context: { queryClient },
  params: { sessionCode },
}: JoinScreenLoaderArgs) => {
  const source = readSourceFromLocation();
  const isRevalidation = isSameShortUrlFlowVisit(
    ShortUrlFlow.Join,
    sessionCode,
  );

  let result;
  try {
    result = await fetchJoinState(queryClient, sessionCode);
  } catch (error) {
    if (isRedirect(error)) throw error;

    if (!isRevalidation) {
      const resolution = getShortUrlResolutionFromError(error);
      trackShortUrlEvent(SHORT_URL_EVENTS.ACCESS_CODE_RESOLVED, {
        access_code: sessionCode,
        session_id: null,
        coach_id: null,
        resolution_status: resolution.resolution_status,
        http_status: resolution.http_status,
        correlation_id: null,
      });
    }
    endShortUrlFlowSession();

    // QueryCache reports terminal API and network failures, even when this
    // route recovers with a connection-problem screen.
    const reportedByReactQuery =
      error instanceof ApiError || isFetchNetworkError(error);
    if (!reportedByReactQuery) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
        { source: "joinScreenLoader", sessionCode },
      );
    }
    if (isFetchNetworkError(error)) throw error;
    throw redirect({ to: "/not-found" });
  }

  const { data } = result;

  // Skip the register when bookNewSession is null so we don't clobber a
  // previously-registered customer_id with undefined.
  if (data.bookNewSession) {
    registerSuperProperties({
      entry_point: DEFAULT_ENTRY_POINT,
      customer_id: data.bookNewSession.customer.id,
    });

    identifyMember({ email: data.bookNewSession.customer.email });
  }

  const joinState = getSessionJoinState(data.state);

  if (!isRevalidation) {
    abandonPreviousShortUrlFlowIfDifferent(ShortUrlFlow.Join, sessionCode);

    const resolution = getShortUrlResolutionFromSuccess();
    trackShortUrlEvent(SHORT_URL_EVENTS.ACCESS_CODE_RESOLVED, {
      access_code: sessionCode,
      session_id: null,
      coach_id: null,
      resolution_status: resolution.resolution_status,
      http_status: resolution.http_status,
      correlation_id: data.correlationId ?? null,
    });

    startShortUrlFlowSession({
      flow: ShortUrlFlow.Join,
      accessCode: sessionCode,
      sessionId: null,
      source,
      correlationId: data.correlationId ?? null,
    });

    trackShortUrlEvent(SHORT_URL_EVENTS.FLOW_ENTERED, {
      flow: ShortUrlFlow.Join,
      session_id: null,
      coach_id: null,
      source,
    });
  }

  try {
    if (
      joinState === SessionJoinState.JoinNow ||
      joinState === SessionJoinState.ReJoin
    ) {
      const trigger =
        joinState === SessionJoinState.JoinNow
          ? ShortUrlMeetRedirectTrigger.AutoT0
          : ShortUrlMeetRedirectTrigger.Rejoin;

      redirectToMeet({
        meetingUrl: data.meetingUrl,
        trigger,
        source,
        timeAccuracyMs:
          trigger === ShortUrlMeetRedirectTrigger.AutoT0
            ? (data.secondsUntilStart ?? 0) * 1000
            : null,
      });
    }

    if (
      joinState === SessionJoinState.Missed ||
      joinState === SessionJoinState.Ended
    ) {
      const previouslyJoinedUrl = localStorage.getItem(
        LAST_JOINED_MEETING_URL_KEY,
      );

      if (!!data.meetingUrl && previouslyJoinedUrl === data.meetingUrl) {
        redirectToMeet({
          meetingUrl: data.meetingUrl,
          trigger: ShortUrlMeetRedirectTrigger.Rejoin,
          source,
          timeAccuracyMs: null,
        });
      }
    }
  } catch (error) {
    if (isRedirect(error)) throw error;
    captureException(
      error instanceof Error ? error : new Error(String(error)),
      { source: "joinScreenLoader", sessionCode },
    );
    throw redirect({ to: "/not-found" });
  }

  return result;
};
